// src/services/timeEntryService.js
// Service-Layer für Zeiteinträge mit Azure Timekeeping Integration

import * as ApiService from './apiService.js';
import {
  saveTimekeepingEntry,
  deleteTimekeepingEntries,
  deleteTimekeepingEntriesByGroup,
} from './azureTimekeepingService.js';

/**
 * Erstellt Zeiteinträge in AbaNinja und speichert parallel in Azure Timekeeping.
 * Bei localOnly === true wird nur in Azure Timekeeping gespeichert (kein AbaNinja-Call).
 */
export const createTimeEntryWithTracking = async (
  selectedEmployee,
  formData,
  timeBlocks,
  activityTypeName,
  contracts,
  localOnly = false
) => {
  const employeeName = `${selectedEmployee.firstName} ${selectedEmployee.lastName}`;

  let contractName = "";
  let fullReference = "";
  if (formData.contractUuid) {
    const contract = contracts.find((c) => c.uuid === formData.contractUuid);
    contractName = contract?.title || contract?.reference || "";
    fullReference = contract?.reference || "";
  }

  // Bei lokalem Projekt: contractName und fullReference aus localProject-Daten
  if (localOnly && formData.localProjectCode) {
    contractName = formData.localProjectName || "";
    fullReference = formData.localProjectCode || "";
  }

  const source = localOnly ? "local" : "abaninja";

  // Gemeinsame ID fuer alle Tage einer Ganztag-Abwesenheit, damit sich der
  // Zeitraum spaeter als Gruppe wieder loeschen laesst.
  const absenceGroupId = formData.isFullDay
    ? (globalThis.crypto?.randomUUID?.() ||
        `grp_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`)
    : "";

  for (const block of timeBlocks) {
    // AbaNinja nur bei nicht-lokalen Einträgen
    if (!localOnly) {
      const entry = {
        employeeUuid: selectedEmployee.uuid,
        activityTypeUuid: formData.activityTypeUuid,
        contractUuid: formData.contractUuid,
        addressUuid: contracts.find((c) => c.uuid === formData.contractUuid)?.addressUuid,
        productUuid: formData.productUuid,
        description: formData.description,
        date: block.date,
        startTime: block.startTime,
        endTime: block.endTime,
        isFullDay: formData.isFullDay,
        startDate: formData.startDate,
        endDate: formData.endDate,
      };

      // Speichern in AbaNinja
      await ApiService.createTimeEntry(entry);
    }

    // Speichern in Azure Timekeeping (nur wenn nicht "Pause")
    if (activityTypeName !== "Pause") {
      try {
        await saveTimekeepingEntry(
          employeeName,
          selectedEmployee.uuid,
          {
            date: block.date,
            startTime: block.startTime,
            endTime: block.endTime,
            description: formData.description,
            contractName: contractName,
            fullReference: fullReference,
            isFullDay: block.isFullDay === true,
            absenceGroupId: block.isFullDay ? absenceGroupId : "",
          },
          activityTypeName,
          source
        );
      } catch (timekeepingError) {
        console.error("Fehler beim Speichern in timekeeping:", timekeepingError);
        // Fehler nur loggen, nicht den gesamten Prozess abbrechen
      }
    }
  }
};

/**
 * Löscht Zeiteinträge aus AbaNinja und Azure Timekeeping.
 * Bei localOnly === true wird nur aus Azure Timekeeping gelöscht (kein AbaNinja-Call).
 */
export const deleteTimeEntryWithTracking = async (employeeUuid, activityUuid, timeEntry, localOnly = false) => {
  // Lösche aus AbaNinja (nur bei nicht-lokalen Einträgen)
  if (!localOnly) {
    await ApiService.deleteTimeEntry(employeeUuid, activityUuid);
  }

  // Lösche aus Azure Timekeeping (wenn nicht "Pause")
  if (timeEntry && timeEntry.activityTypeName !== "Pause") {
    try {
      // Ganztag-Abwesenheit mit Gruppe: komplette Gruppe loeschen (ganzer Zeitraum)
      if (timeEntry.isFullDay && timeEntry.absenceGroupId) {
        await deleteTimekeepingEntriesByGroup(
          employeeUuid,
          timeEntry.absenceGroupId
        );
      } else {
        await deleteTimekeepingEntries(
          employeeUuid,
          timeEntry.date,
          timeEntry.startTime,
          timeEntry.endTime
        );
      }
    } catch (timekeepingError) {
      console.error("Fehler beim Löschen aus timekeeping:", timekeepingError);
      // Fehler nur loggen, nicht den gesamten Delete-Prozess abbrechen
    }
  }
};
